import { type ReactNode, type Ref } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type RecordStatusTone = "active" | "attention" | "inactive" | "neutral";

export interface RecordIdentity {
  /** The record's name, rendered as the page h1 in display type. */
  name: string;
  /** Photo URL or stored `person-photo://` reference (AvatarImage resolves it). Omit to show `initials` (or an icon via `avatarFallback`). */
  avatarUrl?: string | null;
  /** Shown when there's no photo, e.g. "SG". */
  initials?: string;
  /** Replaces the initials fallback, e.g. a building icon for a company. */
  avatarFallback?: ReactNode;
  /** Status dot + label, e.g. { tone: "active", label: "Member" }. */
  status?: { tone: RecordStatusTone; label: string };
  /** Primary contact line beside the status, e.g. "sarah@example.org · (555) 010-2233". */
  contactLine?: ReactNode;
  /** Badge row: type and primary-contact flags. Not decisions — those go in the alert slot. */
  badges?: ReactNode;
  /** Created / updated, inline, e.g. "Created Jan 4, 2024 · Updated 2 days ago". */
  meta?: ReactNode;
}

export interface RecordHeroProps {
  identity: RecordIdentity;
  /** Record actions, right-aligned (stacked under the identity on phones). */
  actions?: ReactNode;
  titleRef?: Ref<HTMLHeadingElement>;
  /**
   * The record is still loading: the hero keeps its shape with skeletons (NAVIGATION_AND_STATES.md,
   * T2 loading: "hero shimmer, tabs real"). The h1 stays, for screen readers and focus, and reads
   * `identity.name` (e.g. "Loading person").
   */
  loading?: boolean;
  className?: string;
}

const DOT: Record<RecordStatusTone, string> = {
  active: "bg-success",
  attention: "bg-warning",
  inactive: "bg-muted-foreground",
  neutral: "bg-border",
};

/**
 * T2 identity hero (docs/design/PAGE_TEMPLATES.md § T2, frame 7b): avatar, name in display
 * type, status dot + contact line, badge row, created/updated inline, actions right.
 *
 * @example
 * <RecordHero
 *   identity={{ name: "Sarah Garcia", initials: "SG", status: { tone: "active", label: "Member" },
 *     contactLine: "sarah@example.org", meta: "Created Jan 4, 2024 · Updated 2 days ago" }}
 *   actions={<Button>Edit</Button>}
 * />
 */
export function RecordHero({ identity, actions, titleRef, loading = false, className }: RecordHeroProps) {
  const { name, avatarUrl, initials, avatarFallback, status, contactLine, badges, meta } = identity;
  // No photo, initials or fallback (a name-only hero, e.g. a forbidden record): no empty circle.
  const hasAvatar = Boolean(avatarUrl || initials || avatarFallback);
  // Loading keeps the same header and h1 elements as the loaded hero (only their contents
  // change), so focus moved to the h1 on arrival survives the data arriving.
  return (
    // Actions sit beside the identity only from 1024px; below that they stack under it, so a long
    // action row can't push the page wider than the viewport (320px reflow).
    <header
      aria-busy={loading || undefined}
      className={cn("flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between", className)}
    >
      <div className="flex min-w-0 items-start gap-4">
        {loading ? (
          <Skeleton className="size-14 shrink-0 rounded-full sm:size-16" />
        ) : !hasAvatar ? null : (
          <Avatar className="size-14 shrink-0 sm:size-16">
            {/* Keyed by source: AvatarImage keeps its last image while a new src resolves, which
                would show the previous record's photo under this record's name. */}
            {avatarUrl ? <AvatarImage key={avatarUrl} src={avatarUrl} alt="" /> : null}
            <AvatarFallback className="bg-muted text-base font-semibold text-muted-foreground">
              {avatarFallback ?? initials}
            </AvatarFallback>
          </Avatar>
        )}
        <div className="min-w-0 space-y-1.5">
          <h1
            ref={titleRef}
            tabIndex={titleRef ? -1 : undefined}
            data-page-arrival={titleRef ? "template" : undefined}
            className="break-words text-page-title font-semibold leading-tight tracking-tight text-foreground focus:outline-none"
          >
            {loading ? (
              <>
                <span className="sr-only">{name}</span>
                <Skeleton className="h-8 w-56 max-w-full" aria-hidden="true" />
              </>
            ) : name}
          </h1>
          {loading ? (
            <>
              <Skeleton className="h-4 w-40 max-w-full" />
              <Skeleton className="h-3 w-32 max-w-full" />
            </>
          ) : null}
          {!loading && (status || contactLine)
            ? (
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                {status
                  ? (
                    <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                      <span className={cn("size-2 shrink-0 rounded-full", DOT[status.tone])} aria-hidden="true" />
                      {status.label}
                    </span>
                  )
                  : null}
                {status && contactLine ? <span aria-hidden="true">·</span> : null}
                {contactLine ? <span className="min-w-0 break-words">{contactLine}</span> : null}
              </p>
            )
            : null}
          {!loading && badges ? <div className="flex flex-wrap items-center gap-2">{badges}</div> : null}
          {!loading && meta ? <p className="text-xs text-[var(--text-subtle)]">{meta}</p> : null}
        </div>
      </div>
      {!loading && actions
        ? (
          // Caller buttons (often size="sm"): 44×44px below 1024px, 24px above (ACCESSIBILITY.md § Target size).
          // [&>div]:flex-wrap: callers often pass their own action group (RecordActions renders one
          // non-wrapping row), which must wrap too.
          <div className="flex min-w-0 flex-wrap items-center gap-2 lg:shrink-0 [&>div]:flex-wrap [&_a]:min-h-11 [&_a]:min-w-11 [&_button]:min-h-11 [&_button]:min-w-11 lg:[&_a]:min-h-6 lg:[&_a]:min-w-6 lg:[&_button]:min-h-6 lg:[&_button]:min-w-6">
            {actions}
          </div>
        )
        : null}
    </header>
  );
}
